import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, Thumbs, Grid, FreeMode, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/grid';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import 'swiper/css/effect-fade';

/**
 * LSElementor - Live Streaming Elementor Integration
 * 
 * This class handles the initialization and management of Swiper sliders
 * across the website, both within Elementor widgets and standalone implementations.
 * 
 * Features:
 * - RTL/LTR direction support with live switching
 * - Lazy loading and performance optimizations
 * - Responsive breakpoints
 * - Support for different slider types
 */
export default class LSElementor {
    constructor() {
        // Core properties
        this.swipers = new Map();
        this.sliderTypes = new Map();
        this.resizeObserver = null;
        this.breakpoints = {
            mobile: 767,
            tablet: 1024
        };
        this.isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
        this.pendingSliders = new Set();

        // Register built-in slider types
        this.registerSliderTypes();

        // Initialize module
        this.init();

        this.eventListeners();

    }

    /**
     * Register all the default slider types with their configuration
     */
    registerSliderTypes() {
        // Channel slider configuration
        this.sliderTypes.set('channel-slider', {
            selector: '.ls-channel-slider',
            defaults: {
                slidesPerView: 4,
                spaceBetween: 20,
                navigation: true,
                pagination: false,
                autoplay: true
            },
            modules: [Navigation, Pagination, Autoplay, Grid],
            responsiveConfig: {
                320: {
                    slidesPerView: 1,
                    spaceBetween: 10,
                    grid: { rows: 1, fill: true }
                },
                576: {
                    slidesPerView: 2,
                    spaceBetween: 15,
                    grid: { rows: 1, fill: true }
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 15,
                    grid: { rows: 1, fill: true }
                }
            },
            elementorWidget: 'ls-channel-slider'
        });

        // Term slider configuration
        this.sliderTypes.set('term-slider', {
            selector: '.ls-term-slider',
            defaults: {
                slidesPerView: 5,
                spaceBetween: 15,
                navigation: true,
                pagination: false,
                autoplay: false,
                freeMode: {
                    enabled: true,
                    minimumVelocity: 0.02,
                    momentum: true,
                    momentumRatio: 0.5
                }
            },
            modules: [Navigation, Pagination, Autoplay, FreeMode],
            responsiveConfig: {
                320: {
                    slidesPerView: 2,
                    spaceBetween: 10
                },
                576: {
                    slidesPerView: 3,
                    spaceBetween: 10
                },
                768: {
                    slidesPerView: 4,
                    spaceBetween: 15
                }
            },
            elementorWidget: 'ls-term-slider'
        });

        // Streaming banner configuration
        this.sliderTypes.set('streaming-banner', {
            selector: '.ls-streaming-banner',
            defaults: {
                slidesPerView: 1,
                spaceBetween: 0,
                effect: 'fade',
                navigation: true,
                pagination: true,
                autoplay: true,
                loop: true,
                fadeEffect: {
                    crossFade: true
                }
            },
            modules: [Navigation, Pagination, Autoplay, Thumbs, EffectFade],
            responsiveConfig: {
                768: {
                    slidesPerView: 1,
                }
            },
            elementorWidget: 'ls-streaming-banner',
            hasThumbsGallery: true
        });

        // Default custom slider configuration
        this.sliderTypes.set('custom', {
            selector: '[data-ls-slider="true"], .ls-custom-swiper',
            defaults: {
                slidesPerView: 1,
                spaceBetween: 0,
                navigation: true,
                pagination: true,
                autoplay: false
            },
            modules: [Navigation, Pagination],
            responsiveConfig: {
                320: {
                    slidesPerView: 1,
                    spaceBetween: 10
                },
                576: {
                    slidesPerView: 2,
                    spaceBetween: 15
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 15
                }
            }
        });
    }

    eventListeners() {
        this.$modal = jQuery('#lsShareModal');
        this.$body = jQuery('body');
        jQuery(document.body).on('click', '#ls-share-model', this.openModal.bind(this));
        jQuery(document.body).on('click', '#lsCloseModal', this.closeModal.bind(this));
        jQuery(document.body).on('click', '#lsShareModal', this.outsideClick.bind(this));
        jQuery(document.body).on('click', '.ls-copy-btn', this.copyToClipboard.bind(this));
    }

    openModal(e) {
        e.preventDefault();
        this.$modal.css('display', 'flex');
        this.$body.addClass('ls-modal-open');
    }

    closeModal() {
        this.$modal.css('display', 'none');
        this.$body.removeClass('ls-modal-open');
    }

    outsideClick(e) {
        if (jQuery(e.target).is('#lsShareModal')) {
            this.closeModal();
        }
    }

    copyToClipboard() {
        const $input = jQuery('#lsCopyInput');
        $input[0].select();
        $input[0].setSelectionRange(0, 99999); // For mobile
        document.execCommand('copy');

        const $btn = jQuery('.ls-copy-btn');
        const original = $btn.text();
        $btn.text('Copied!');
        setTimeout(() => $btn.text(original), 2000);
    }

    /**
     * Register a new slider type for use with the system
     * @param {String} type - Unique identifier for this slider type
     * @param {Object} config - Configuration object for the slider type
     */
    registerSliderType(type, config) {
        if (!type || typeof type !== 'string') {
            console.error('Slider type must be a string');
            return;
        }

        if (!config || typeof config !== 'object') {
            console.error('Slider config must be an object');
            return;
        }

        // Set default configuration values if missing
        const defaultConfig = {
            selector: `.ls-${type}`,
            defaults: {
                slidesPerView: 1,
                spaceBetween: 0,
                navigation: true,
                pagination: true,
                autoplay: false
            },
            modules: [Navigation, Pagination],
            responsiveConfig: {},
            elementorWidget: null
        };

        // Merge with provided config
        const mergedConfig = { ...defaultConfig, ...config };

        // Register the new slider type
        this.sliderTypes.set(type, mergedConfig);

        // If already initialized, scan for any instances of this new type
        if (document.readyState !== 'loading') {
            this.initSlidersOfType(type);
        }

        return true;
    }

    /**
     * Initialize the module
     */
    init() {
        // Wait for Elementor frontend to be ready
        if (window.elementorFrontend) {
            this.onElementorFrontendReady();
        } else {
            window.addEventListener('elementor/frontend/init', () => this.onElementorFrontendReady());
        }

        // Initialize non-Elementor sliders as soon as possible
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', this.initNonElementorSliders.bind(this));
        } else {
            this.initNonElementorSliders();
        }

        // Watch for sliders that need initialization when they become visible
        this.setupIntersectionObserver();

        // Set up direction observer to handle RTL/LTR changes
        this.setupDirectionObserver();

        // Expose API methods for external use
        if (window.LS === undefined) {
            window.LS = {};
        }

        window.LS.Sliders = {
            registerSliderType: this.registerSliderType.bind(this),
            refreshSlider: this.refreshSlider.bind(this),
            getSliderInstance: (id) => this.swipers.get(id),
            getAllSliders: () => Array.from(this.swipers.values()),
            updateDirection: (direction) => this.handleDirectionChange(direction)
        };
    }

    /**
     * Handle Elementor frontend initialization
     */
    onElementorFrontendReady() {
        // Set up mutation observer to handle dynamically added elements
        this.setupMutationObserver();

        // Set up resize observer for responsive behavior
        this.setupResizeObserver();

        // Initialize widgets that are already in the viewport
        this.initVisibleSliders();

        // Register Elementor widget handlers
        this.registerElementorWidgetHandlers();
    }

    /**
     * Register handlers for Elementor widgets
     */
    registerElementorWidgetHandlers() {
        if (!window.elementorFrontend || !window.elementorFrontend.hooks) {
            return;
        }

        // Register handlers for each slider type that has an Elementor widget
        this.sliderTypes.forEach((config, type) => {
            if (config.elementorWidget) {
                window.elementorFrontend.hooks.addAction(
                    'frontend/element_ready/' + config.elementorWidget + '.default',
                    ($element) => {

                        this.refreshWidget($element, type);
                    }
                );
            }
        });
    }

    /**
     * Initialize sliders that are visible in the viewport
     */
    initVisibleSliders() {
        this.sliderTypes.forEach((config, type) => {
            document.querySelectorAll(`${config.selector}:not(.swiper-initialized):not(.ls-pending-init)`)
                .forEach(slider => this.handleSliderInViewport(slider, type));
        });
    }

    /**
     * Initialize sliders that are not managed by Elementor
     */
    initNonElementorSliders() {
        this.sliderTypes.forEach((config, type) => {
            if (type !== 'custom') {
                document.querySelectorAll(`${config.selector}:not(.elementor-widget-wrap *):not(.swiper-initialized):not(.ls-pending-init)`)
                    .forEach(slider => {
                        const sliderType = slider.dataset.lsSliderType || type;

                        this.handleSliderInViewport(slider, sliderType);
                    });
            }
        });

        // Always handle custom sliders last
        const customConfig = this.sliderTypes.get('custom');
        if (customConfig) {
            document.querySelectorAll(`${customConfig.selector}:not(.swiper-initialized):not(.ls-pending-init)`)
                .forEach(slider => {
                    const sliderType = slider.dataset.lsSliderType || 'custom';
                    this.handleSliderInViewport(slider, sliderType);
                });
        }
    }

    /**
     * Set up intersection observer to initialize sliders when they become visible
     */
    setupIntersectionObserver() {
        if ('IntersectionObserver' in window) {
            const options = {
                root: null,
                rootMargin: '100px',
                threshold: 0.01
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const slider = entry.target;
                        // Determine slider type
                        let type = 'custom';

                        this.sliderTypes.forEach((config, sliderType) => {
                            if (slider.matches(config.selector)) {
                                type = slider.dataset.lsSliderType || sliderType;
                            }
                        });

                        this.initializeSlider(slider, type);
                        observer.unobserve(slider);
                    }
                });
            }, options);

            // Mark pending sliders and observe them
            this.markAndObserveSliders(observer);
        } else {
            // Fallback for browsers that don't support IntersectionObserver
            this.initAllSliders();
        }
    }

    /**
     * Initialize all registered slider types
     */
    initAllSliders() {
        this.sliderTypes.forEach((config, type) => {
            this.initSlidersOfType(type);
        });
    }

    /**
     * Initialize all sliders of a specific type
     * @param {string} type - The slider type to initialize
     */
    initSlidersOfType(type) {
        const config = this.sliderTypes.get(type);
        if (!config) return;

        document.querySelectorAll(`${config.selector}:not(.swiper-initialized):not(.ls-pending-init)`)
            .forEach(slider => this.initializeSlider(slider, type));
    }

    /**
     * Mark sliders as pending initialization and observe them
     * @param {IntersectionObserver} observer - The intersection observer
     */
    markAndObserveSliders(observer) {
        this.sliderTypes.forEach((config, type) => {
            document.querySelectorAll(`${config.selector}:not(.swiper-initialized):not(.ls-pending-init)`)
                .forEach(slider => {
                    slider.classList.add('ls-pending-init');
                    this.pendingSliders.add(slider);
                    observer.observe(slider);
                });
        });
    }

    /**
     * Handle a slider that is in the viewport
     * @param {HTMLElement} slider - The slider element
     * @param {string} type - The slider type
     */
    handleSliderInViewport(slider, type) {
        // Remove pending class and initialize the slider
        slider.classList.remove('ls-pending-init');
        this.pendingSliders.delete(slider);

        // Initialize the slider
        this.initializeSlider(slider, type);
    }

    /**
     * Initialize specific slider based on its type
     * @param {HTMLElement} slider - The slider element
     * @param {string} type - The slider type
     */
    initializeSlider(slider, type) {

        // Basic validation for all slider types
        if (!slider || !document.body.contains(slider)) {
            console.warn('Slider element is not in the DOM or is not valid', slider);
            return;
        }

        // Check if the slider has the necessary structure
        if (!slider.querySelector('.swiper-wrapper')) {
            console.warn('Slider is missing required .swiper-wrapper element', slider);
            return;
        }

        // Check if the slider has any slides
        const slides = slider.querySelectorAll('.swiper-slide');
        if (slides.length === 0) {
            console.warn('Slider has no slides (.swiper-slide elements)', slider);
            return;
        }

        // Apply layout protection to prevent layout shifts
        this.applyLayoutProtection(slider, type);

        // Get slider configuration
        const config = this.sliderTypes.get(type) || this.sliderTypes.get('custom');
        if (!config) {
            console.warn(`No configuration found for slider type: ${type}`);
            return;
        }

        // Create a new unique ID for this slider
        const uniqueId = `ls-${type}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        slider.dataset.lsSliderId = uniqueId;

        try {
            // Initialize based on type
            this.createSwiperInstance(slider, type, config);
        } catch (error) {
            console.error(`Error initializing ${type} slider:`, error);
            slider.classList.remove('ls-pending-init');
            slider.classList.add('ls-slider-error');

            // Add error message for admins/devs
            if (window.isUserAdmin || document.body.classList.contains('logged-in')) {
                const errorMsg = document.createElement('div');
                errorMsg.className = 'ls-slider-error-message';
                errorMsg.textContent = `Slider initialization error: ${error.message}`;
                slider.appendChild(errorMsg);
            }
        }
    }

    /**
     * Create a Swiper instance for a slider
     * @param {HTMLElement} slider - The slider element
     * @param {string} type - The slider type
     * @param {Object} config - The slider configuration
     */
    createSwiperInstance(slider, type, config) {
        // Get slider settings from data attributes or use defaults
        const settings = this.getSliderSettings(slider, config.defaults);

        // Create or find navigation elements
        const navigationElements = this.ensureNavigationElements(slider, settings);

        // Handle thumbs slider if applicable
        let thumbsSwiper = null;
        if (config.hasThumbsGallery) {
            thumbsSwiper = this.initializeThumbsSlider(slider);
        }
        // Ensure we have all necessary modules
        const modules = [...new Set([...config.modules])];

        // Add optional modules based on settings
        if (settings.autoplay && !modules.includes(Autoplay)) modules.push(Autoplay);
        if (settings.grid && !modules.includes(Grid)) modules.push(Grid);
        if (settings.freeMode && !modules.includes(FreeMode)) modules.push(FreeMode);
        if (settings.effect === 'fade' && !modules.includes(EffectFade)) modules.push(EffectFade);
        if (thumbsSwiper && !modules.includes(Thumbs)) modules.push(Thumbs);


        // Preload critical images to reduce layout shift
        this.preloadCriticalImages(slider, type);

        // Build swiper configuration
        const swiperConfig = this.buildSwiperConfig(
            slider,
            settings,
            modules,
            navigationElements,
            thumbsSwiper,
            config.responsiveConfig,
            type
        );


        // Initialize Swiper with configuration
        const swiper = new Swiper(slider, swiperConfig);

        // Store swiper instance for later reference
        this.swipers.set(slider.dataset.lsSliderId, swiper);

        // Make available to jQuery/DOM
        if (window.jQuery) {
            window.jQuery(slider).data('swiper', swiper);
        }
        slider.swiper = swiper;

        // Dispatch event that slider was initialized
        slider.dispatchEvent(new CustomEvent('ls-slider-initialized', {
            detail: {
                type,
                instance: swiper,
                element: slider
            }
        }));
    }

    /**
     * Ensure navigation elements exist
     * @param {HTMLElement} slider - The slider element
     * @param {Object} settings - The slider settings
     * @returns {Object} - Object with navigation elements
     */
    ensureNavigationElements(slider, settings) {
        let navigationPrevEl = slider.querySelector('.swiper-button-prev');
        let navigationNextEl = slider.querySelector('.swiper-button-next');
        let paginationEl = slider.querySelector('.swiper-pagination');

        // Create navigation elements if navigation is enabled and they don't exist
        if (settings.navigation) {
            if (!navigationPrevEl) {
                navigationPrevEl = document.createElement('div');
                navigationPrevEl.className = 'swiper-button-prev';
                slider.appendChild(navigationPrevEl);
            }

            if (!navigationNextEl) {
                navigationNextEl = document.createElement('div');
                navigationNextEl.className = 'swiper-button-next';
                slider.appendChild(navigationNextEl);
            }
        }

        if (settings.pagination && !paginationEl) {
            paginationEl = document.createElement('div');
            paginationEl.className = 'swiper-pagination';
            slider.appendChild(paginationEl);
        }

        return { navigationPrevEl, navigationNextEl, paginationEl };
    }

    /**
     * Initialize thumbs slider if available
     * @param {HTMLElement} slider - The main slider element
     * @returns {Swiper|null} - Thumbs swiper instance or null
     */
    initializeThumbsSlider(slider) {
        const thumbsSlider = slider.querySelector('.ls-banner-thumbs');
        if (!thumbsSlider) return null;

        return new Swiper(thumbsSlider, {
            modules: [FreeMode, Thumbs],
            spaceBetween: 10,
            slidesPerView: 'auto',
            freeMode: true,
            watchSlidesProgress: true,
            preloadImages: true,
            rtl: this.isRTL,
            lazy: true,
            breakpoints: {
                320: {
                    slidesPerView: 3,
                    spaceBetween: 5
                },
                576: {
                    slidesPerView: 4
                },
                768: {
                    slidesPerView: 5
                }
            }
        });
    }

    /**
 * Build Swiper configuration object
 * @param {HTMLElement} slider - The slider element
 * @param {Object} settings - The slider settings
 * @param {Array} modules - The Swiper modules to use
 * @param {Object} navElements - The navigation elements
 * @param {Swiper|null} thumbsSwiper - The thumbs slider instance
 * @param {Object} responsiveConfig - Responsive configuration
 * @param {string} type - The slider type
 * @returns {Object} - The Swiper configuration object
 */
    buildSwiperConfig(slider, settings, modules, navElements, thumbsSwiper, responsiveConfig, type) {
        // Extract navigation elements
        const { navigationPrevEl, navigationNextEl, paginationEl } = navElements;

        // Build base configuration
        const config = {
            modules,
            slidesPerView: settings.slidesPerView,
            spaceBetween: settings.spaceBetween,
            slidesPerGroup: settings.slidesPerGroup || 1,
            loop: settings.loop || false,
            preloadImages: true,
            updateOnImagesReady: true,
            lazy: {
                loadPrevNext: true,
                loadPrevNextAmount: 2,
                loadOnTransitionStart: true
            },
            speed: settings.speed || (type === 'streaming-banner' ? 800 : 500),
            rtl: this.isRTL,
            grid: modules.includes(Grid) ? (settings.grid || { rows: 1, fill: true }) : undefined,
            effect: settings.effect || 'slide',
            fadeEffect: settings.fadeEffect || (modules.includes(EffectFade) ? { crossFade: true } : undefined),
            freeMode: settings.freeMode || (modules.includes(FreeMode) ? {
                enabled: true,
                minimumVelocity: 0.02,
                momentum: true,
                momentumRatio: 0.5
            } : undefined),
            autoplay: settings.autoplay && modules.includes(Autoplay) ? {
                delay: settings.autoplayDelay || (type === 'streaming-banner' ? 6000 : 5000),
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            } : false,
            navigation: settings.navigation ? {
                nextEl: navigationNextEl,
                prevEl: navigationPrevEl
            } : false,
            watchOverflow: true,
            pagination: settings.pagination ? {
                el: paginationEl,
                clickable: true,
                dynamicBullets: settings.paginationDynamicBullets || type === 'streaming-banner'
            } : false,
            thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
            breakpoints: this.mergeBreakpoints(settings.breakpoints, responsiveConfig, slider),
            on: {
                init: () => {
                    this.handleSliderInit(slider, type);
                    requestAnimationFrame(() => {
                        slider.style.opacity = '1';
                        slider.style.transition = 'opacity 0.3s ease';
                    });
                },
                lazyImageReady: (slideEl, imageEl) => {
                    imageEl.style.transition = 'opacity 0.5s ease';
                    imageEl.style.opacity = 1;
                },
                imagesReady: (swiper) => {
                    swiper.update();
                }
            },
        };

        // Apply specific settings for 'streaming-banner' type
        if (type === 'streaming-banner') {
            config.slidesPerView = 1;
            config.spaceBetween = 0;
            config.loop = true;

            if (modules.includes(Autoplay)) {
                config.autoplay = {
                    delay: settings.autoplayDelay || 6000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true
                };
            }

            if (settings.pagination && paginationEl) {
                config.pagination = {
                    el: paginationEl,
                    clickable: true,
                    dynamicBullets: true
                };
            }

            if (config.grid) {
                delete config.grid;
            }
        }

        // Remove any undefined/null properties
        return Object.fromEntries(Object.entries(config).filter(([_, v]) => v !== undefined && v !== null));
    }

    /**
     * Merge breakpoints from settings and default responsive config
     * @param {Object} settingsBreakpoints - Breakpoints from settings
     * @param {Object} defaultBreakpoints - Default breakpoints for the slider type
     * @returns {Object} - Merged breakpoints
     */
    mergeBreakpoints(settingsBreakpoints, defaultBreakpoints, element = null) {
        // Use settings breakpoints if provided
        if (settingsBreakpoints && Object.keys(settingsBreakpoints).length > 0) {
            return this.adjustBreakpoints(settingsBreakpoints);
        }

        // Read responsive values from data attributes first (higher priority than defaults)
        let desktopSlides     = 4;
        let laptopSlides      = 3;
        let tabletSlides      = 2;
        let mobileSlides      = 1;
        let spaceBetween      = 20;
        let hasDataAttributes = false;

        if (element) {
            // Check if element has responsive data attributes
            if (element.dataset.slidesPerView || element.dataset.slidesPerViewLaptop ||
                element.dataset.slidesPerViewTablet || element.dataset.slidesPerViewMobile) {
                hasDataAttributes = true;

                desktopSlides = parseInt(element.dataset.slidesPerView) || desktopSlides;
                laptopSlides  = parseInt(element.dataset.slidesPerViewLaptop) || laptopSlides;
                tabletSlides  = parseInt(element.dataset.slidesPerViewTablet) || tabletSlides;
                mobileSlides  = parseInt(element.dataset.slidesPerViewMobile) || mobileSlides;
                spaceBetween  = parseInt(element.dataset.spaceBetween) || spaceBetween;

            }
        }

        // If we have data attributes, use them
        if (hasDataAttributes) {
            return {
                320: {
                    slidesPerView: mobileSlides,
                    spaceBetween: 10
                },
                576: {
                    slidesPerView: tabletSlides,
                    spaceBetween: 15
                },
                768: {
                    slidesPerView: laptopSlides,
                    spaceBetween: 15
                },
                1024: {
                    slidesPerView: desktopSlides,
                    spaceBetween: spaceBetween
                }
            };
        }

        // Use default breakpoints if available and no data attributes found
        if (defaultBreakpoints && Object.keys(defaultBreakpoints).length > 0) {
            return defaultBreakpoints;
        }

        // Final fallback to basic responsive config
        return {
            320: {
                slidesPerView: mobileSlides,
                spaceBetween: 10
            },
            576: {
                slidesPerView: tabletSlides,
                spaceBetween: 15
            },
            768: {
                slidesPerView: laptopSlides,
                spaceBetween: 15
            },
            1024: {
                slidesPerView: desktopSlides,
                spaceBetween: spaceBetween
            }
        };
    }

    /**
     * Apply layout protection to prevent layout shifts
     * @param {HTMLElement} slider - The slider element
     * @param {string} type - The slider type
     */
    applyLayoutProtection(slider, type) {
        // If we're in Elementor editor, add special classes
        const isInEditor = window.elementorFrontend?.isEditMode?.() || false;

        // Apply container classes to prevent layout shifts
        slider.closest('.elementor-widget-wrap')?.classList.add('ls-swiper-container');
        slider.closest('.elementor-widget')?.classList.add('ls-widget-swiper');

        // Temporarily hide the slider to prevent layout shift
        if (!slider.style.opacity) {
            slider.style.opacity = '0';
        }

        slider.classList.add('ls-swiper-initializing');
    }

    /**
     * Preload critical images to reduce layout shifts
     * @param {HTMLElement} slider - The slider element
     * @param {string} type - The slider type
     */
    preloadCriticalImages(slider, type) {
        const slides = slider.querySelectorAll('.swiper-slide');
        const maxSlidesToPreload = type === 'term-slider' ? 5 : 3;
        const imagesToPreload = Math.min(slides.length, maxSlidesToPreload);

        for (let i = 0; i < imagesToPreload; i++) {
            const slide = slides[i];
            const images = slide.querySelectorAll('img[data-src], img.swiper-lazy');

            images.forEach(img => {
                // Set explicit width and height attributes if missing but available
                if (!img.hasAttribute('width') && img.naturalWidth) {
                    img.setAttribute('width', img.naturalWidth);
                }
                if (!img.hasAttribute('height') && img.naturalHeight) {
                    img.setAttribute('height', img.naturalHeight);
                }

                // Handle lazy loading
                if (img.dataset.src) {
                    // Create a new image object to preload
                    const preloadImg = new Image();
                    preloadImg.onload = () => {
                        // Once preloaded, set the actual src
                        img.src = img.dataset.src;
                        // Clean up data attributes
                        img.removeAttribute('data-src');
                        img.classList.add('swiper-lazy-loaded');
                    };
                    // Start preloading
                    preloadImg.src = img.dataset.src;
                }
            });
        }
    }

    /**
     * Extract slider settings from data attributes
     * @param {HTMLElement} element - The slider element
     * @param {Object} defaults - Default settings
     * @returns {Object} - Combined settings
     */
    getSliderSettings(element, defaults = {}) {
        const settings = { ...defaults };

        try {
            // Try to parse settings from data-settings attribute
            const dataSettings = element.dataset.settings;
            if (dataSettings) {
                const parsedSettings = JSON.parse(dataSettings);
                Object.assign(settings, parsedSettings);
            }

            // For custom sliders with data-swiper-config
            const swiperConfig = element.dataset.swiperConfig;
            if (swiperConfig) {
                const parsedConfig = JSON.parse(swiperConfig);
                Object.assign(settings, parsedConfig);
            }

            // Process individual data attributes (take precedence over data-settings)
            this.processDataAttributes(element, settings);

        } catch (e) {
            console.warn('Error parsing slider settings:', e);
        }

        return settings;
    }

    /**
     * Process data attributes from the element
     * @param {HTMLElement} element - The element with data attributes
     * @param {Object} settings - The settings object to update
     */
    processDataAttributes(element, settings) {
        // Basic settings
        if (element.dataset.slidesPerView) settings.slidesPerView = parseInt(element.dataset.slidesPerView, 10);
        if (element.dataset.spaceBetween) settings.spaceBetween = parseInt(element.dataset.spaceBetween, 10);
        if (element.dataset.slidesPerGroup) settings.slidesPerGroup = parseInt(element.dataset.slidesPerGroup, 10);

        // Boolean settings
        if (element.dataset.autoplay) settings.autoplay = element.dataset.autoplay === 'true';
        if (element.dataset.loop) settings.loop = element.dataset.loop === 'true';
        if (element.dataset.navigation) settings.navigation = element.dataset.navigation === 'true';
        if (element.dataset.pagination) settings.pagination = element.dataset.pagination === 'true';

        // Numeric settings
        if (element.dataset.autoplayDelay) settings.autoplayDelay = parseInt(element.dataset.autoplayDelay, 10);
        if (element.dataset.speed) settings.speed = parseInt(element.dataset.speed, 10);

        // Other settings
        if (element.dataset.effect) settings.effect = element.dataset.effect;
        if (element.dataset.direction) settings.direction = element.dataset.direction;
        if (element.dataset.breakpoints) settings.breakpoints = JSON.parse(element.dataset.breakpoints);

        // Grid settings
        if (element.dataset.grid === 'true') {
            settings.grid = {
                rows: parseInt(element.dataset.gridRows || 1, 10),
                fill: element.dataset.gridFill === 'true' || element.dataset.gridFill === undefined
            };
        }

        // FreeMode settings
        if (element.dataset.freeMode === 'true') {
            settings.freeMode = {
                enabled: true,
                minimumVelocity: parseFloat(element.dataset.freeModeMinimumVelocity || 0.02),
                momentum: element.dataset.freeModeMomentum !== 'false'
            };
        }
    }

    /**
     * Handle slider initialization
     * @param {HTMLElement} slider - The slider element
     * @param {string} type - Slider type
     */
    handleSliderInit(slider, type) {
        // Add appropriate classes
        slider.classList.add('ls-slider-ready');
        slider.classList.remove('ls-swiper-initializing');

        // Animate items initially hidden
        setTimeout(() => {
            const items = slider.querySelectorAll('.swiper-slide');
            items.forEach((item, i) => {
                item.style.transitionDelay = `${i * 50}ms`;
                item.classList.add('ls-slide-visible');
            });
        }, 100);

        // Dispatch custom event
        const event = new CustomEvent('ls-slider:initialized', {
            detail: {
                type: type,
                element: slider,
                id: slider.dataset.lsSliderId
            }
        });
        document.dispatchEvent(event);
    }

    /**
     * Refresh a specific widget
     * @param {Element} element - The widget element
     * @param {string} type - Widget type
     */
    refreshWidget(element, type) {
        // Destroy existing swiper instance if it exists
        const slider = element.querySelector('.swiper');
        if (slider && slider.dataset.lsSliderId) {
            const existingSwiper = this.swipers.get(slider.dataset.lsSliderId);
            if (existingSwiper) {
                existingSwiper.destroy();
                this.swipers.delete(slider.dataset.lsSliderId);
            }
            slider.classList.remove('swiper-initialized', 'ls-slider-ready', 'ls-pending-init');
            slider.removeAttribute('data-ls-slider-id');
        }

        // Initialize the slider immediately since it's being refreshed by Elementor
        if (slider) {
            this.initializeSlider(slider, type);
        }
    }

    /**
     * Refresh a specific slider
     * @param {string} id - Slider ID or selector
     * @param {string} type - Optional type override
     */
    refreshSlider(id, type = null) {
        let slider;

        // Handle both ID and selector
        if (id.startsWith('#') || id.startsWith('.')) {
            slider = document.querySelector(id);
        } else {
            slider = document.querySelector(`[data-ls-slider-id="${id}"]`);
        }

        if (!slider) {
            console.warn(`Slider not found: ${id}`);
            return;
        }

        // Get the current type if not provided
        if (!type) {
            this.sliderTypes.forEach((config, sliderType) => {
                if (slider.matches(config.selector)) {
                    type = sliderType;
                }
            });
        }

        // Destroy the existing swiper instance
        const existingSwiper = this.swipers.get(slider.dataset.lsSliderId);
        if (existingSwiper) {
            existingSwiper.destroy();
            this.swipers.delete(slider.dataset.lsSliderId);
        }

        // Reset the slider
        slider.classList.remove('swiper-initialized', 'ls-slider-ready', 'ls-pending-init');

        // Re-initialize the slider
        this.initializeSlider(slider, type || 'custom');
    }

    /**
     * Adjust breakpoints to match Elementor's style of handling breakpoints
     * @param {Object} breakpoints - Breakpoint settings
     * @returns {Object} - Adjusted breakpoint settings
     */
    adjustBreakpoints(breakpoints) {
        if (!breakpoints || Object.keys(breakpoints).length === 0) {
            return breakpoints;
        }

        // Sort breakpoints from smallest to largest
        const sortedBreakpoints = Object.keys(breakpoints)
            .map(point => parseInt(point, 10))
            .sort((a, b) => a - b);

        // Create new breakpoints object with proper min-width format for Swiper
        const newBreakpoints = {};

        sortedBreakpoints.forEach(breakpoint => {
            // Handle Elementor's way of defining breakpoints
            newBreakpoints[breakpoint] = breakpoints[breakpoint.toString()];
        });

        return newBreakpoints;
    }

    /**
     * Set up resize observer for responsive behavior
     */
    setupResizeObserver() {
        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const slider = entry.target;
                    const sliderId = slider.dataset.lsSliderId;
                    if (sliderId) {
                        const swiper = this.swipers.get(sliderId);
                        if (swiper) {
                            // Update swiper on container resize
                            swiper.update();

                            // Check if we need to adjust for mobile/desktop
                            const currentWidth = entry.contentRect.width;
                            const wasMobile = slider.classList.contains('ls-slider-mobile');
                            const isMobile = currentWidth <= this.breakpoints.mobile;

                            if (isMobile && !wasMobile) {
                                slider.classList.add('ls-slider-mobile');
                                swiper.update();
                            } else if (!isMobile && wasMobile) {
                                slider.classList.remove('ls-slider-mobile');
                                swiper.update();
                            }
                        }
                    }
                }
            });

            // Start observing initialized sliders
            this.observeInitializedSliders();
        }
    }

    /**
     * Observe sliders that have been initialized
     */
    observeInitializedSliders() {
        document.querySelectorAll('[data-ls-slider-id]').forEach(slider => {
            if (this.resizeObserver && !slider.classList.contains('ls-observed')) {
                this.resizeObserver.observe(slider);
                slider.classList.add('ls-observed');
            }
        });
    }

    /**
     * Set up mutation observer to handle dynamically added elements
     */
    setupMutationObserver() {
        const observer = new MutationObserver(mutations => {
            let needsInit = false;

            mutations.forEach(mutation => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // Check if this node is a slider or contains a slider
                            let hasSlider = false;

                            // Check if the node itself is a slider
                            this.sliderTypes.forEach((config, type) => {
                                if (node.matches && node.matches(config.selector)) {
                                    hasSlider = true;
                                }
                            });

                            // If not, check if it contains a slider
                            if (!hasSlider && node.querySelector) {
                                // Build a selector for all uninitialized sliders
                                const selectors = Array.from(this.sliderTypes.values())
                                    .map(config => `${config.selector}:not(.swiper-initialized):not(.ls-pending-init)`)
                                    .join(', ');

                                if (node.querySelector(selectors)) {
                                    hasSlider = true;
                                }
                            }

                            if (hasSlider) {
                                needsInit = true;
                            }
                        }
                    });
                }
            });

            if (needsInit) {
                // Mark and observe new sliders
                if ('IntersectionObserver' in window) {
                    const options = {
                        root: null,
                        rootMargin: '100px',
                        threshold: 0.01
                    };

                    const observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                            if (entry.isIntersecting) {
                                const slider = entry.target;

                                // Determine slider type
                                let type = 'custom';
                                this.sliderTypes.forEach((config, sliderType) => {
                                    if (slider.matches(config.selector)) {
                                        type = slider.dataset.lsSliderType || sliderType;
                                    }
                                });

                                this.initializeSlider(slider, type);
                                observer.unobserve(slider);
                            }
                        });
                    }, options);

                    this.markAndObserveSliders(observer);
                } else {
                    // Fallback for browsers that don't support IntersectionObserver
                    this.initAllSliders();
                }

                // Observe newly added sliders for resize
                this.observeInitializedSliders();
            }
        });

        // Start observing the document with the configured parameters
        observer.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * Clean up resources when needed
     */
    destroy() {
        // Destroy all swiper instances
        this.swipers.forEach(swiper => {
            if (swiper && typeof swiper.destroy === 'function') {
                swiper.destroy(true, false);
            }
        });
        this.swipers.clear();

        // Disconnect observers
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    /**
     * Detect direction changes (RTL/LTR) and reinitialize sliders if needed
     * This is especially useful when users switch languages or use RTL/LTR toggles
     */
    setupDirectionObserver() {
        // Store initial direction
        this.initialDirection = document.dir || document.documentElement.dir || 'ltr';

        // Setup mutation observer to detect direction changes on HTML or body elements
        const directionObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.attributeName === 'dir') {
                    const currentDirection = document.dir || document.documentElement.dir || 'ltr';
                    if (this.initialDirection !== currentDirection) {
                        // Direction has changed, reinitialize all sliders

                        this.handleDirectionChange(currentDirection);
                        this.initialDirection = currentDirection;
                    }
                }
            });
        });

        // Start observing html and body elements for direction changes
        directionObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });
        directionObserver.observe(document.body, { attributes: true, attributeFilter: ['dir'] });

        // Also check for direction on load/DOMContentLoaded
        window.addEventListener('load', () => this.checkAndHandleDirection());

        // Listen for custom direction change events
        document.addEventListener('ls-direction-change', (event) => {
            if (event.detail && event.detail.direction) {
                this.handleDirectionChange(event.detail.direction);
            }
        });
    }

    /**
     * Check and handle direction on page load
     */
    checkAndHandleDirection() {
        const currentDirection = document.dir || document.documentElement.dir || 'ltr';
        this.isRTL = currentDirection === 'rtl';

        // Update all existing sliders with correct direction
        this.swipers.forEach(swiper => {
            if (swiper.params.rtl !== this.isRTL) {
                swiper.params.rtl = this.isRTL;
                swiper.rtl = this.isRTL;
                swiper.update();
            }
        });
    }

    /**
     * Handle direction change by reinitializing sliders
     * @param {string} newDirection - The new direction
     */
    handleDirectionChange(newDirection) {
        this.isRTL = newDirection === 'rtl';

        // Store sliders to reinit
        const slidersToReinit = [];

        // Store slider data before destroying them
        this.swipers.forEach((swiper, id) => {
            const sliderElement = swiper.el;
            if (sliderElement) {
                // Determine the slider type
                let type = 'custom';
                this.sliderTypes.forEach((config, sliderType) => {
                    if (sliderElement.matches(config.selector)) {
                        type = sliderElement.dataset.lsSliderType || sliderType;
                    }
                });

                slidersToReinit.push({ element: sliderElement, type: type });
            }
        });

        // Destroy all swiper instances WITHOUT cleaning our custom inline styles
        this.destroy();

        // Reinitialize sliders
        slidersToReinit.forEach(({ element, type }) => {

            element.querySelectorAll('.swiper-slide').forEach(slide =>
                ['opacity', 'transform', 'transitionProperty', 'transitionDuration'].forEach(p => slide.style[p] = '')
            );

            const wrapper = element.querySelector('.swiper-wrapper');
            if (wrapper) ['transform', 'transitionDuration'].forEach(p => wrapper.style[p] = '');

            this.initializeSlider(element, type);
        });

        // Dispatch event for other components that might need to know
        document.dispatchEvent(new CustomEvent('ls-direction-changed', {
            detail: { direction: newDirection, isRTL: this.isRTL }
        }));
    }

}